import { USER_STATUSES } from 'constants/index';
import moment from 'moment';
import { GeneralIds, RoutePaths } from 'types/types';
import {
  ConditionOperation,
  ConditionValueType,
  FieldAttributes,
  FieldTemplate,
  FieldType,
} from 'api/models/FieldTemplate';
import { DateFieldDateTypes } from 'components/GlobalDialog/AddFieldTemplate/utils';

export const getRoutePath = (path: RoutePaths, ids?: GeneralIds) => {
  return (
    path
      .replace(':projectId', ids?.projectId ?? '')
      .replace(':formTemplateTypeId', ids?.formTemplateTypeId ?? '')
      .replace(':visitId', ids?.visitId ?? '')
      .replace(':formId', ids?.formId ?? '')
      .replace(':patientId', ids?.patientId ?? '')
      .replace(':userId', ids?.userId ?? '')
      // remove trailing  slash
      .replace(/\/+$/, '')
  );
};

export const getUserStatuses = (t: (key: string) => string) => {
  return USER_STATUSES.map(status => ({
    ...status,
    label: t(status.i18nLabelKey),
  }));
};

export const getDateString = (date: string | Date | undefined) => {
  if (!date) return '';
  return moment(date).format('YYYY.MM.DD');
};

export const getDateTimeString = (
  date: string | Date | undefined,
  format?: string,
) => {
  if (!date) return '';
  return moment(date).format(format ?? 'YYYY.MM.DD HH:mm');
};

export const getId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export function removeEmpty(obj: Object): FieldAttributes {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) => v != null && v !== '' && v !== undefined,
    ),
  ) as FieldAttributes;
}

export function isUUID(value: string) {
  return /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    value,
  );
}

export function getConditionValueType(
  field?: FieldTemplate | null,
): ConditionValueType {
  switch (field?.fieldType) {
    case FieldType.Number:
      return ConditionValueType.NUMBER;
    case FieldType.Date:
      switch (field?.fieldAttributes.dateType) {
        case DateFieldDateTypes.Time:
          return ConditionValueType.TIME;
        case DateFieldDateTypes.DateTime:
          return ConditionValueType.DATETIME;
        case DateFieldDateTypes.Year:
          return ConditionValueType.YEAR;
        case DateFieldDateTypes.Date:
        default:
          return ConditionValueType.DATE;
      }
    case FieldType.Select:
    case FieldType.StringList:
      return ConditionValueType.STRING_LIST;
    case FieldType.Text:
    default:
      return ConditionValueType.STRING;
  }
}

export function getOperatorByType(
  type?: ConditionValueType,
): ConditionOperation[] {
  switch (type) {
    case ConditionValueType.NUMBER:
      return [
        ConditionOperation.EQUALS,
        ConditionOperation.NOT_EQUALS,
        ConditionOperation.GREATER_THAN,
        ConditionOperation.GREATER_THAN_OR_EQUAL,
        ConditionOperation.LOWER_THAN,
        ConditionOperation.LOWER_THAN_OR_EQUAL,
      ];
    case ConditionValueType.DATE:
    case ConditionValueType.DATETIME:
    case ConditionValueType.TIME:
    case ConditionValueType.YEAR:
      return [
        ConditionOperation.EQUALS,
        ConditionOperation.NOT_EQUALS,
        ConditionOperation.GREATER_THAN,
        ConditionOperation.GREATER_THAN_OR_EQUAL,
        ConditionOperation.LOWER_THAN,
        ConditionOperation.LOWER_THAN_OR_EQUAL,
      ];
    case ConditionValueType.STRING_LIST:
      return [ConditionOperation.IS_ANY_OF];

    case ConditionValueType.STRING:
    default:
      return [ConditionOperation.EQUALS, ConditionOperation.NOT_EQUALS];
  }
}

export function isFieldEmpty(value?: string | string[] | null): boolean {
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  
  return !value || value.trim() === '';
}